import AppModal from "components/Modal";
import React, { useState, useEffect } from 'react';
import ReactDOM from "react-dom";
import { useAuth } from '../../../helpers/authContext';
import { Button } from "reactstrap";
import { useToast } from "components/ui/use-toast";
import useModal from "helpers/useModal";
import LoginModal from "features/user/userModals/LoginModal";
import { isLoggedWithKeychain } from "services/dsteem-service";
import { keychainWitnessVote } from "helpers/keychain";
import { witnessWifVote } from "helpers/steem";

type Props = {
    isShowing: boolean;
    hide: () => void;
    witnessName: string;
    onSuccess?: () => void;
};

export const ConfirmWitnessVoteModal = ({ isShowing, hide, witnessName, onSuccess = undefined }: Props) => {
    const { user, autologin } = useAuth();
    const [requiredActive, setRequiredActive] = useState<any>(true);
    const [isVoting, setIsVoting] = useState(false);
    const keychainLogin = isLoggedWithKeychain(autologin);
    const { isOpenLoginModal, toggleLoginModal } = useModal();
    const { toast } = useToast();
    const [userData, setUserData] = useState<any>(null);

    // Fetch user data to check current witness votes
    useEffect(() => {
        if (isShowing && user) {
            import("services/dsteem-service").then(module => {
                module.getAccount(user).then((acc: any) => {
                    if (acc && acc.length > 0) {
                        setUserData(acc[0]);
                    }
                });
            });
        }
    }, [isShowing, user]);

    const isVoted = userData?.witness_votes?.includes(witnessName);

    const handleVote = async (wif = "") => {
        if (!witnessName) return;
        
        const voteStatus = true; // Support witness

        if (isVoted) {
            toast({
                title: "Already Supported",
                description: `You are already voting for ${witnessName}.`,
                variant: "wild_green"
            });
            hide();
            return;
        }

        setIsVoting(true);

        try {
            let success = false;
            let errorMessage = "";

            if (keychainLogin) {
                const res = await keychainWitnessVote(voteStatus, user, witnessName);
                success = res === true;
                if (!success) errorMessage = "Failed to process vote with Keychain.";
            } else {
                const res: any = await witnessWifVote(voteStatus, autologin, witnessName, wif);
                success = res.success;
                if (!success) {
                    if (res?.result?.message?.toString().includes("Missing Active Authority") || res?.result?.message?.toString().includes("Missing Posting Authority")) {
                        setRequiredActive(true); // actually it requires active key
                        toggleLoginModal();
                        setIsVoting(false);
                        return;
                    } else {
                        errorMessage = res.result || "Failed to process vote.";
                    }
                }
            }

            if (!success) {
                toast({
                    title: "Failed voting for witness.",
                    description: errorMessage,
                    variant: "rose_red"
                });
            } else {
                toast({
                    title: "Witness supported successfully.",
                    description: `Thank you for supporting ${witnessName}.`,
                    variant: "wild_green"
                });

                if (onSuccess) {
                    onSuccess();
                }
                hide();
            }
        } catch (error: any) {
            console.error("Voting error:", error);
            toast({ title: "Error", description: "An unexpected error occurred.", variant: "rose_red" });
        } finally {
            setIsVoting(false);
        }
    };

    return isShowing ? ReactDOM.createPortal(
        <React.Fragment>
            <AppModal isShowing={isShowing} hide={() => !isVoting && hide()} size="md" header="Confirm Witness Vote">
                <div className="p-3 text-scheme">
                    <p className="text-muted">
                        You are about to cast your vote to support <strong>@{witnessName}</strong> as a Steem Witness.
                    </p>
                    <p className="text-muted">
                        Please confirm your action below.
                    </p>
                    <div className="d-flex gap-3 mt-4">
                        <Button color="secondary" onClick={() => hide()} disabled={isVoting} className="w-100 mr-2 btn-fancy">
                            Cancel
                        </Button>
                        <Button color="default" onClick={() => handleVote()} disabled={isVoting || isVoted} className="w-100 btn-fancy position-relative">
                            {isVoting ? (
                                <span className="spinner dark m-0" />
                            ) : keychainLogin ? (
                                <>
                                Confirm with
                                <img className="icon icon-sm ml-1" src="/assets/img/keychain.svg" />
                                </>
                            ) : (
                                "Confirm"
                            )}
                        </Button>
                    </div>
                </div>
            </AppModal>
            <LoginModal
                isShowing={isOpenLoginModal}
                hide={toggleLoginModal}
                requiredActive={requiredActive}
                onSuccess={({ wif }) => {
                    if (wif) {
                        handleVote(wif);
                    }
                }}
            />
        </React.Fragment>, document.body
    ) : null;
};
